import { forwardRef, useImperativeHandle, useRef } from 'react';
import { VideoPlayerHandle } from 'src/components';
import * as S from './styles';

/** Bias the sharp subject left so most spare blur sits under the options column. */
export const TEST_VIDEO_OBJECT_POSITION = '25% 0%';

type FullscreenBackgroundVideoProps = {
  src: string;
  autoPlay?: boolean;
  objectPosition?: string;
  'data-testid'?: string;
  overlayTestId?: string;
  onPlay?: (details?: { duration: number }) => void;
  onEnded?: () => void;
};

/**
 * Full-viewport learnable video: a sharp, height-filling subject over a
 * blurred cover of the same clip (cinematic side fill, no black mattes).
 */
export const FullscreenBackgroundVideo = forwardRef<
  VideoPlayerHandle,
  FullscreenBackgroundVideoProps
>(
  (
    {
      src,
      autoPlay = true,
      objectPosition,
      'data-testid': dataTestId,
      overlayTestId,
      onPlay,
      onEnded,
    },
    ref,
  ) => {
    const sharpRef = useRef<VideoPlayerHandle>(null);
    const blurRef = useRef<VideoPlayerHandle>(null);

    useImperativeHandle(
      ref,
      () => ({
        replayAtSpeed: rate => {
          sharpRef.current?.replayAtSpeed(rate);
          blurRef.current?.replayAtSpeed(rate);
        },
      }),
      [],
    );

    return (
      <S.VideoContainer data-testid={dataTestId} $objectPosition={objectPosition}>
        <S.VideoBackdropLayer aria-hidden data-testid="presentationVideoBackdrop">
          <S.VideoBackdrop
            ref={blurRef}
            autoPlay={autoPlay}
            muted
            hidePlayButton
            playOnKeyboardShortcut={false}
            src={src}
          />
        </S.VideoBackdropLayer>
        <S.VideoPlayer
          ref={sharpRef}
          autoPlay={autoPlay}
          hidePlayButton
          progressiveQuality
          playOnKeyboardShortcut
          src={src}
          onPlay={onPlay}
          onEnded={onEnded}
        />
        <S.VideoOverlay data-testid={overlayTestId} />
      </S.VideoContainer>
    );
  },
);

FullscreenBackgroundVideo.displayName = 'FullscreenBackgroundVideo';
